import { useState, useEffect, useCallback } from 'react';
import { getAnnouncements } from '../services/api';

/** The only place fetch logic for announcements lives — every contextual
 * embed (university/country pages, scholarships, program search, footer)
 * and every other announcements surface goes through this hook.
 *
 * Pass `skip: true` while a required param (e.g. a slug the parent page is
 * still loading) isn't known yet — this holds off the request entirely
 * rather than firing one with an incomplete filter, so a page that never
 * ends up having anything to show never wastes a request either. */
export default function useAnnouncements(params = {}, { skip = false } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);

  // Stable across renders even when callers pass a fresh object literal
  // each time — only the actual param values should retrigger a fetch.
  const key = JSON.stringify(params);

  const load = useCallback((signal) => {
    if (skip) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getAnnouncements(params, { signal })
      .then((data) => {
        setItems(data.results || (Array.isArray(data) ? data : []));
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setError(err.message);
      })
      .finally(() => setLoading(false));
    // `key` (params, stringified) is the real dependency here, not `params`
    // itself — a fresh object literal each render would otherwise refetch
    // every render regardless of whether its contents actually changed.
  }, [key, skip]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return { items, loading, error };
}
